import { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api/admin";

// ---- fetch helper -----------------------------------------------------------
async function adminFetch(path, opts = {}, password) {
  const res = await fetch(API_BASE + path, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Password": password || "",
      ...(opts.headers || {}),
    },
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error(`http_${res.status}`);
  return res.json();
}

// ---- helpers ----------------------------------------------------------------
const fmt = (ts) => (ts ? new Date(ts).toLocaleString("en-IN", { hour12: true }) : "-");
const fmtTime = (ts) => (ts ? new Date(ts).toLocaleTimeString("en-IN", { hour12: true }) : "-");

const EVENT_STYLES = {
  inbound_message:  { label: "user",       tint: "#F3F4F6", dir: "left" },
  instant_ack:      { label: "ack",        tint: "#DCFCE7", dir: "right" },
  agent_reply:      { label: "agent",      tint: "#E0F2F1", dir: "right" },
  delight_flourish: { label: "delight",    tint: "#FDF4FF", dir: "right" },
  engagement_ping:  { label: "ping",       tint: "#FEF3C7", dir: "right" },
  session_end:      { label: "close",      tint: "#FEE2E2", dir: "right" },
  off_scope_hit:    { label: "off-scope",  tint: "#FEE2E2", dir: "left" },
  stage_change:     { label: "stage",      tint: "#F3F4F6", dir: "meta" },
  error:            { label: "error",      tint: "#FEE2E2", dir: "meta" },
};

// ---- login gate -------------------------------------------------------------
function AdminLogin({ onOk }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await adminFetch("/auth/check", {}, pw);
      sessionStorage.setItem("vzor_admin_pw", pw);
      onOk(pw);
    } catch (e) {
      setErr("wrong password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{minHeight:"100vh", display:"grid", placeItems:"center", background:"#F3F4F6"}}>
      <form onSubmit={submit} style={{
        background:"#fff", padding:"32px 28px", borderRadius:12,
        border:"1px solid #E5E7EB", boxShadow:"0 4px 14px rgba(17,24,39,0.06)",
        width:340,
      }}>
        <div style={{color:"#0F766E", fontWeight:700, fontSize:20, marginBottom:4}}>VZOR</div>
        <h1 style={{fontSize:22, fontWeight:700, margin:"0 0 4px"}}>Audit Portal</h1>
        <p style={{color:"#6B7280", fontSize:13, marginTop:0, marginBottom:20}}>
          Read-only. Shared password required.
        </p>
        <input
          type="password" value={pw} onChange={(e)=>setPw(e.target.value)}
          placeholder="ADMIN_PASSWORD" autoFocus
          data-testid="admin-password-input"
          style={{width:"100%", padding:"12px 14px", borderRadius:8, border:"1px solid #E5E7EB", fontSize:14}}
        />
        {err && <div style={{color:"#B91C1C", fontSize:13, marginTop:10}} data-testid="admin-login-error">{err}</div>}
        <button type="submit" disabled={busy} data-testid="admin-login-submit"
          style={{marginTop:16, width:"100%", background:"#0F766E", color:"#fff",
            padding:"12px", borderRadius:8, fontWeight:600, border:"none",
            cursor:busy?"wait":"pointer"}}>
          {busy ? "checking…" : "Enter portal"}
        </button>
      </form>
    </div>
  );
}

// ---- badges -----------------------------------------------------------------
const Badge = ({ children, color="#0F766E", bg="#E0F2F1", testid }) => (
  <span data-testid={testid}
    style={{background:bg, color, fontSize:11, padding:"2px 8px", borderRadius:999,
      fontWeight:600, letterSpacing:".02em"}}>{children}</span>
);

// ---- stats cards ------------------------------------------------------------
function StatsCards({ stats }) {
  if (!stats) return null;
  const cards = [
    { k: "conversations", v: stats.total_conversations, testid:"stat-conversations" },
    { k: "numbers",       v: stats.unique_numbers, testid:"stat-numbers" },
    { k: "returning",     v: stats.returning_user_count, testid:"stat-returning" },
    { k: "delight fires", v: stats.delight_fires, testid:"stat-delight" },
    { k: "off-scope",     v: stats.off_scope_hits, testid:"stat-offscope" },
    { k: "avg turns",     v: stats.avg_turns_per_conversation, testid:"stat-avg-turns" },
  ];
  return (
    <div data-testid="stats-cards"
      style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",
        gap:12, padding:"16px 20px", background:"#FFFFFF", borderBottom:"1px solid #E5E7EB"}}>
      {cards.map(c=>(
        <div key={c.k} data-testid={c.testid}
          style={{background:"#F3F4F6", borderRadius:10, padding:"12px 14px"}}>
          <div style={{color:"#6B7280", fontSize:12, textTransform:"uppercase", letterSpacing:".08em"}}>{c.k}</div>
          <div style={{fontSize:24, fontWeight:700, color:"#111827"}}>{c.v}</div>
        </div>
      ))}
    </div>
  );
}

// ---- testers list -----------------------------------------------------------
function TestersList({ testers, selectedWa, onSelect, query }) {
  const filtered = useMemo(() => {
    const q = (query||"").toLowerCase().trim();
    if (!q) return testers;
    return testers.filter(t =>
      (t.wa_number||"").includes(q) ||
      (t.profile_name||"").toLowerCase().includes(q) ||
      (t.manual_label||"").toLowerCase().includes(q));
  }, [testers, query]);

  return (
    <div data-testid="testers-list"
      style={{borderRight:"1px solid #E5E7EB", overflowY:"auto"}}>
      {filtered.length === 0 && (
        <div style={{padding:20, color:"#6B7280", fontSize:14}}>No testers match.</div>
      )}
      {filtered.map(t => (
        <div key={t.wa_number}
          onClick={()=>onSelect(t.wa_number)}
          data-testid={`tester-row-${t.wa_number}`}
          style={{
            padding:"14px 16px", borderBottom:"1px solid #E5E7EB", cursor:"pointer",
            background: selectedWa===t.wa_number ? "#F3F4F6" : "#fff",
          }}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline"}}>
            <div style={{fontWeight:600, color:"#111827"}}>
              {t.manual_label || t.profile_name || "unknown"}
            </div>
            <div style={{fontSize:11, color:"#6B7280"}}>{fmt(t.last_seen)}</div>
          </div>
          <div style={{fontSize:12, color:"#6B7280", marginTop:2}}>{t.wa_number}</div>
          <div style={{display:"flex", gap:6, marginTop:8, flexWrap:"wrap"}}>
            {t.furthest_stage_reached && <Badge testid={`badge-stage-${t.wa_number}`}>stage: {t.furthest_stage_reached}</Badge>}
            <Badge testid={`badge-convs-${t.wa_number}`} color="#111827" bg="#F3F4F6">
              {t.total_conversations} convs · {t.total_messages} msgs
            </Badge>
            {t.selfie_sent && <Badge testid={`badge-selfie-${t.wa_number}`} color="#7C3AED" bg="#F5F3FF">selfie</Badge>}
            {t.off_scope_hit && <Badge testid={`badge-offscope-${t.wa_number}`} color="#B91C1C" bg="#FEE2E2">off-scope</Badge>}
            {t.bought && <Badge testid={`badge-bought-${t.wa_number}`} color="#166534" bg="#DCFCE7">bought</Badge>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- transcript view --------------------------------------------------------
function ConversationBubble({ ev }) {
  const style = EVENT_STYLES[ev.event_type] || { label: ev.event_type, tint:"#F3F4F6", dir:"meta" };
  const meta = ev.meta || {};
  const align = style.dir === "left" ? "flex-start" : style.dir === "right" ? "flex-end" : "center";
  const maxW = style.dir === "meta" ? "80%" : "72%";
  return (
    <div style={{display:"flex", justifyContent:align, padding:"6px 0"}}>
      <div data-testid={`bubble-${ev.event_type}`}
        style={{background:style.tint, borderRadius:12, padding:"10px 14px", maxWidth:maxW,
          border:"1px solid #E5E7EB"}}>
        <div style={{display:"flex", gap:8, alignItems:"center", marginBottom:4}}>
          <Badge color="#111827" bg="#fff" testid={`bubble-label-${ev.event_type}`}>{style.label}</Badge>
          {ev.agent && <Badge testid={`bubble-agent-${ev.agent}`}>agent: {ev.agent}</Badge>}
          {meta.routing_reason && (
            <span style={{fontSize:11, color:"#6B7280"}}>· {meta.routing_reason}</span>
          )}
          {meta.updated_stage && (
            <Badge testid="bubble-stage" color="#6B7280" bg="#F3F4F6">→ {meta.updated_stage}</Badge>
          )}
          <span style={{marginLeft:"auto", color:"#9CA3AF", fontSize:11}}>{fmtTime(ev.ts)}</span>
        </div>
        <div style={{whiteSpace:"pre-wrap", fontSize:14, color:"#111827", lineHeight:1.45}}>
          {ev.content || <em style={{color:"#9CA3AF"}}>(no content)</em>}
        </div>
      </div>
    </div>
  );
}

function Transcript({ conv, onLabelSave }) {
  const [editing, setEditing] = useState(false);
  const [labelVal, setLabelVal] = useState("");

  useEffect(() => {
    setLabelVal(conv?.manual_label || "");
    setEditing(false);
  }, [conv?.wa_number]);

  if (!conv) return (
    <div style={{padding:40, color:"#6B7280", fontSize:14}} data-testid="transcript-empty">
      Select a tester on the left to view their conversations.
    </div>
  );

  return (
    <div data-testid="transcript-panel" style={{overflowY:"auto", padding:"16px 24px"}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
        borderBottom:"1px solid #E5E7EB", paddingBottom:12, marginBottom:16}}>
        <div>
          <div style={{fontSize:20, fontWeight:700, color:"#111827"}}>
            {conv.manual_label || conv.profile_name || "unknown"}
          </div>
          <div style={{color:"#6B7280", fontSize:13}}>{conv.wa_number} · {conv.conversations.length} conversation(s)</div>
        </div>
        <div>
          {!editing ? (
            <button data-testid="edit-label-btn" onClick={()=>setEditing(true)}
              style={{padding:"8px 14px", borderRadius:8, border:"1px solid #E5E7EB",
                background:"#fff", fontWeight:600, cursor:"pointer"}}>
              {conv.manual_label ? "Edit label" : "Add label"}
            </button>
          ) : (
            <div style={{display:"flex", gap:8}}>
              <input value={labelVal} onChange={(e)=>setLabelVal(e.target.value)}
                data-testid="label-input" placeholder="e.g. Hemang – Lenskart"
                style={{padding:"8px 12px", border:"1px solid #E5E7EB", borderRadius:8, fontSize:14}} />
              <button data-testid="save-label-btn"
                onClick={async ()=>{ await onLabelSave(conv.wa_number, labelVal); setEditing(false); }}
                style={{padding:"8px 14px", borderRadius:8, background:"#0F766E", color:"#fff",
                  fontWeight:600, border:"none", cursor:"pointer"}}>Save</button>
            </div>
          )}
        </div>
      </div>

      {conv.conversations.map(c => (
        <div key={c.conversation_id} style={{marginBottom:32}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
            <div style={{fontWeight:600, color:"#111827"}}>Conversation · {fmt(c.first_ts)}</div>
            <div style={{display:"flex", gap:6}}>
              <Badge testid={`conv-turns-${c.conversation_id}`} color="#111827" bg="#F3F4F6">
                {c.turn_count} turns
              </Badge>
              {c.stage && <Badge testid={`conv-stage-${c.conversation_id}`}>{c.stage}</Badge>}
              {c.is_returning_user && <Badge testid="conv-returning" color="#7C3AED" bg="#F5F3FF">returning</Badge>}
            </div>
          </div>
          <div style={{border:"1px solid #E5E7EB", borderRadius:12, padding:"12px 16px", background:"#fff"}}>
            {c.events.map((ev, idx) => (
              <ConversationBubble key={`${ev.ts}_${idx}`} ev={ev} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- main portal ------------------------------------------------------------
export default function AdminPortal() {
  const [pw, setPw] = useState(() => sessionStorage.getItem("vzor_admin_pw") || "");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [testers, setTesters] = useState([]);
  const [labels, setLabels] = useState({});
  const [selectedWa, setSelectedWa] = useState(null);
  const [convData, setConvData] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const authCheck = useCallback(async (p) => {
    try { await adminFetch("/auth/check", {}, p); return true; }
    catch { return false; }
  }, []);

  useEffect(() => {
    if (!pw) { setLoading(false); return; }
    (async () => {
      const ok = await authCheck(pw);
      if (!ok) { setPw(""); sessionStorage.removeItem("vzor_admin_pw"); setLoading(false); return; }
      setAuthed(true);
      const [s, t, l] = await Promise.all([
        adminFetch("/stats", {}, pw),
        adminFetch("/testers", {}, pw),
        adminFetch("/labels", {}, pw),
      ]);
      setStats(s);
      const labelMap = Object.fromEntries(l.labels.map(x=>[x.wa_number, x.label]));
      setLabels(labelMap);
      setTesters(t.testers.map(x => ({...x, manual_label: labelMap[x.wa_number] || x.manual_label})));
      setLoading(false);
    })();
  }, [pw, authCheck]);

  const onSelectTester = async (wa) => {
    setSelectedWa(wa);
    const r = await adminFetch(`/by-number/${wa}`, {}, pw);
    r.manual_label = labels[wa] || null;
    setConvData(r);
  };

  const onLabelSave = async (wa, label) => {
    await adminFetch("/label", {
      method: "PUT",
      body: JSON.stringify({wa_number: wa, label}),
    }, pw);
    // refresh labels + testers + current conv
    const [l, t] = await Promise.all([
      adminFetch("/labels", {}, pw),
      adminFetch("/testers", {}, pw),
    ]);
    const labelMap = Object.fromEntries(l.labels.map(x=>[x.wa_number, x.label]));
    setLabels(labelMap);
    setTesters(t.testers.map(x => ({...x, manual_label: labelMap[x.wa_number] || x.manual_label})));
    if (selectedWa === wa) {
      setConvData(prev => prev ? {...prev, manual_label: labelMap[wa] || null } : prev);
    }
  };

  if (loading && !authed) return <div data-testid="admin-loading" style={{padding:40}}>Loading…</div>;
  if (!authed) return <AdminLogin onOk={(p)=>{ setPw(p); setAuthed(true); }} />;

  return (
    <div data-testid="admin-portal" style={{
      minHeight:"100vh", display:"grid",
      gridTemplateRows:"auto auto 1fr",
      background:"#FFFFFF",
    }}>
      {/* Top bar */}
      <div style={{padding:"16px 20px", borderBottom:"1px solid #E5E7EB",
        display:"flex", justifyContent:"space-between", alignItems:"center"}}>
        <div style={{display:"flex", gap:14, alignItems:"baseline"}}>
          <span style={{color:"#0F766E", fontWeight:700, fontSize:22}}>VZOR</span>
          <span style={{color:"#6B7280", fontSize:14}}>Audit Portal · read-only</span>
        </div>
        <div style={{display:"flex", gap:12, alignItems:"center"}}>
          <input placeholder="search number / name / label"
            data-testid="admin-search"
            value={query} onChange={(e)=>setQuery(e.target.value)}
            style={{padding:"8px 12px", border:"1px solid #E5E7EB", borderRadius:8, fontSize:14, minWidth:280}} />
          <button data-testid="admin-logout"
            onClick={() => { sessionStorage.removeItem("vzor_admin_pw"); window.location.reload(); }}
            style={{padding:"8px 14px", borderRadius:8, border:"1px solid #E5E7EB",
              background:"#fff", fontWeight:600, cursor:"pointer"}}>Log out</button>
        </div>
      </div>

      <StatsCards stats={stats} />

      <div style={{display:"grid", gridTemplateColumns:"340px 1fr", minHeight:0}}>
        <TestersList testers={testers} selectedWa={selectedWa} onSelect={onSelectTester} query={query} />
        <Transcript conv={convData} onLabelSave={onLabelSave} />
      </div>
    </div>
  );
}
